import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '../cn';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  padded?: boolean;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { className, padded = true, children, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn('pos-card', padded && 'p-4', className)}
      {...rest}
    >
      {children}
    </div>
  );
});

export interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
}

export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(function CardHeader(
  { className, title, subtitle, actions, children, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(
        'flex items-start justify-between gap-3 border-b border-slate-200 pb-3 mb-3 dark:border-slate-800',
        className,
      )}
      {...rest}
    >
      <div className="min-w-0 flex-1">
        {title && (
          <h3 className="text-pos-lg font-semibold text-slate-900 dark:text-slate-100 truncate">
            {title}
          </h3>
        )}
        {subtitle && (
          <p className="text-pos-sm text-slate-500 dark:text-slate-400 truncate">{subtitle}</p>
        )}
        {children}
      </div>
      {actions && <div className="shrink-0 flex items-center gap-2">{actions}</div>}
    </div>
  );
});

export const CardBody = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardBody({ className, children, ...rest }, ref) {
    return (
      <div ref={ref} className={cn('text-pos-base', className)} {...rest}>
        {children}
      </div>
    );
  },
);

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  function CardFooter({ className, children, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center justify-end gap-2 border-t border-slate-200 pt-3 mt-3 dark:border-slate-800',
          className,
        )}
        {...rest}
      >
        {children}
      </div>
    );
  },
);
